/// <amd-module name="Wit/Common/TeamSettingsPanel/Components/TeamSettingsGeneralTab" />
import * as React from "react";
import { IReadonlyObservableValue, ObservableValue } from "VSS/Core/Observable";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { Accordion, IAccordionSection } from "VSSUI/Accordion";
import { Checkbox } from "VSSUI/Checkbox";
import { FormItem } from "VSSUI/FormItem";
import { Icon } from "VSSUI/Icon";
import { ILinkProps, Link } from "VSSUI/Link";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Observer } from "VSSUI/Observer";
import { PanelOverlay } from "VSSUI/Panel";
import { RadioButton, RadioButtonGroup } from "VSSUI/RadioButton";
import { Spinner } from "VSSUI/Spinner";
import * as Resources from "Wit/Common/TeamSettingsPanel/Resources";
import { ITeamPanelSettings, ITeamPermissions } from "../Contracts/Contracts";
import { ITeamSettingsPanelService, getTeamSettingsPanelService } from "../Services/TeamSettingsPanelService";

export interface ITeamSettingsGeneralTabProps {
    teamId: string;
}

export class TeamSettingsGeneralTab extends VssComponent<ITeamSettingsGeneralTabProps> {
    private disableBugsAndWorkingDays = new ObservableValue<boolean>(false);

    private backlogLevelValidationErrorState = new ObservableValue<boolean>(false);
    private backlogLevelValidationMessage = new ObservableValue<string>("");

    private teamSettings: IReadonlyObservableValue<ITeamPanelSettings | undefined>;
    private teamSettingsPanelService: ITeamSettingsPanelService;

    private workingDaysValidationErrorState = new ObservableValue<boolean>(false);
    private workingDaysValidationMessage = new ObservableValue<string>("");

    constructor(props: ITeamSettingsGeneralTabProps, context: IVssComponentContext) {
        super(props, context);

        this.teamSettingsPanelService = getTeamSettingsPanelService(context.pageContext);

        // make sure we have fresh panel settings object in case we edited it earlier and cancelled
        this.teamSettings = this.teamSettingsPanelService.getTeamPanelSettings({ ignoreCache: true });
        this.updateBugsAndWorkingDays();
    }

    public render(): JSX.Element {
        return (
            <Observer
                teamPanelSettings={{
                    observableValue: this.teamSettings,
                    filter: this.onTeamSettingsChange
                }}
            >
                {() => {
                    const isBugBehaviorValid = this.teamSettings.value?.isBugsBehaviorValid;

                    const sections: IAccordionSection[] = [
                        {
                            isCollapsed: new ObservableValue(false),
                            key: "backlogs-section",
                            onRenderContent: () => getSectionContent(Resources.BacklogsDescription, undefined, this.getBacklogsContent()),
                            title: Resources.Backlogs
                        },
                        {
                            isCollapsed: new ObservableValue(false),
                            key: "working-days-section",
                            onRenderContent: () => getSectionContent(Resources.WorkingDaysDescription, undefined, this.getWorkingDaysContent()),
                            title: Resources.WorkingDays
                        }
                    ];

                    if (isBugBehaviorValid) {
                        sections.push({
                            isCollapsed: new ObservableValue(false),
                            key: "working-bugs-section",
                            onRenderContent: () =>
                                getSectionContent(
                                    Resources.WorkingWithBugsDescription,
                                    { href: "https://go.microsoft.com/fwlink/?LinkId=2014729", title: Resources.WorkingWithBugsDescriptionLink },
                                    this.getBugsContent()
                                ),
                            title: Resources.WorkingWithBugs
                        });
                    }

                    return (
                        <>
                            <Observer permissions={this.teamSettingsPanelService.getTeamPermissions(this.props.teamId)}>
                                {(props: { permissions: ITeamPermissions | undefined }) => {
                                    const { permissions } = props;
                                    const disabled = !permissions?.CurrentUserHasTeamAdminPermission;
                                    return disabled ? (
                                        <MessageCard className="flex-noshrink margin-bottom-16" severity={MessageCardSeverity.Warning}>
                                            <div className="flex-column font-size-ms">{Resources.InsufficientPermissionsBacklogsWarning}</div>
                                        </MessageCard>
                                    ) : null;
                                }}
                            </Observer>
                            <Observer isBusy={this.teamSettingsPanelService.getIsBusy()}>
                                {(props: { isBusy: boolean }) => {
                                    return (
                                        <>
                                            {props.isBusy && <PanelOverlay overlayContent={<Spinner />} />}
                                            <Accordion
                                                sections={sections}
                                                className="team-settings-panel-general"
                                                onSectionClicked={this.onSectionClicked}
                                            />
                                        </>
                                    );
                                }}
                            </Observer>
                        </>
                    );
                }}
            </Observer>
        );
    }

    private getBacklogsContent = () => {
        return (
            <Observer permissions={this.teamSettingsPanelService.getTeamPermissions(this.props.teamId)}>
                {(props: { permissions: ITeamPermissions | undefined }) => {
                    const { permissions } = props;
                    const teamPanelSettings = this.teamSettings.value;
                    const disabled = !permissions?.CurrentUserHasTeamAdminPermission;

                    if (!teamPanelSettings) {
                        return null;
                    }

                    return (
                        <FormItem
                            label={Resources.BacklogsNavigationLevels}
                            message={this.backlogLevelValidationMessage}
                            error={this.backlogLevelValidationErrorState}
                        >
                            <div className="rhythm-vertical-8 flex-column">
                                {teamPanelSettings.backlogVisibilitySettings.map(level => {
                                    return (
                                        <Checkbox
                                            key={level.id}
                                            onChange={(event, checked) => {
                                                if (level.checked.value !== checked) {
                                                    if (!checked && level.isRequirementmentBacklog) {
                                                        this.disableBugsAndWorkingDays.value = true;
                                                    } else if (checked && level.isRequirementmentBacklog) {
                                                        this.disableBugsAndWorkingDays.value = false;
                                                    }
                                                    level.checked.value = checked;
                                                    this.validate(teamPanelSettings);
                                                    this.teamSettingsPanelService.updateIsDirty();
                                                }
                                            }}
                                            checked={level.checked}
                                            ariaLabel={level.name}
                                            label={
                                                <span>
                                                    <Icon
                                                        ariaHidden={true}
                                                        className="icon-margin vertical-bottom"
                                                        iconName="Backlog"
                                                        style={{ color: level.color }}
                                                    />
                                                    <span>{level.name}</span>
                                                </span>
                                            }
                                            disabled={disabled}
                                        />
                                    );
                                })}
                            </div>
                        </FormItem>
                    );
                }}
            </Observer>
        );
    };

    private getBugsContent = () => {
        return (
            <Observer
                disableBugsAndWorkingDays={this.disableBugsAndWorkingDays}
                permissions={this.teamSettingsPanelService.getTeamPermissions(this.props.teamId)}
            >
                {(props: { disableBugsAndWorkingDays: boolean; permissions: ITeamPermissions | undefined }) => {
                    const { disableBugsAndWorkingDays, permissions } = props;
                    const teamPanelSettings = this.teamSettings.value;
                    const disabled = !permissions?.CurrentUserHasTeamAdminPermission;

                    if (!teamPanelSettings) {
                        return null;
                    }

                    return (
                        <FormItem message={disableBugsAndWorkingDays ? Resources.DisabledDueToBacklogSelection : undefined}>
                            <RadioButtonGroup
                                onSelect={selectedId => {
                                    if (teamPanelSettings.bugsBehavior.value !== selectedId) {
                                        teamPanelSettings.bugsBehavior.value = selectedId;
                                        this.teamSettingsPanelService.updateIsDirty();
                                    }
                                }}
                                selectedButtonId={teamPanelSettings.bugsBehavior}
                            >
                                <RadioButton
                                    id="1"
                                    text={Resources.BugsRequirements}
                                    key="option0"
                                    tooltipProps={{ text: Resources.BugsRequirementsTooltip }}
                                    disabled={disableBugsAndWorkingDays || disabled}
                                />
                                <RadioButton
                                    id="2"
                                    text={Resources.BugsTasks}
                                    key="option1"
                                    tooltipProps={{ text: Resources.BugsTasksTooltip }}
                                    disabled={disableBugsAndWorkingDays || disabled}
                                />
                                <RadioButton
                                    id="0"
                                    text={Resources.BugsNotManaged}
                                    key="option2"
                                    tooltipProps={{ text: Resources.BugsNotManagedTooltip }}
                                    disabled={disableBugsAndWorkingDays || disabled}
                                />
                            </RadioButtonGroup>
                        </FormItem>
                    );
                }}
            </Observer>
        );
    };

    private getWorkingDaysContent = () => {
        return (
            <Observer
                disableBugsAndWorkingDays={this.disableBugsAndWorkingDays}
                permissions={this.teamSettingsPanelService.getTeamPermissions(this.props.teamId)}
            >
                {(props: { disableBugsAndWorkingDays: boolean; permissions: ITeamPermissions | undefined }) => {
                    const { disableBugsAndWorkingDays, permissions } = props;
                    const teamPanelSettings = this.teamSettings.value;
                    const disabled = !permissions?.CurrentUserHasTeamAdminPermission;

                    if (!teamPanelSettings) {
                        return null;
                    }

                    return (
                        <FormItem
                            label={Resources.SelectDays}
                            message={disableBugsAndWorkingDays ? Resources.DisabledDueToBacklogSelection : this.workingDaysValidationMessage}
                            error={this.workingDaysValidationErrorState}
                        >
                            <div className="rhythm-vertical-8 flex-column">
                                {teamPanelSettings.workingDays.map(workingDay => {
                                    return (
                                        <Checkbox
                                            key={workingDay.id}
                                            onChange={(event, checked) => {
                                                if (workingDay.isWorkday.value !== checked) {
                                                    workingDay.isWorkday.value = checked;
                                                    this.validate(teamPanelSettings);
                                                    this.teamSettingsPanelService.updateIsDirty();
                                                }
                                            }}
                                            checked={workingDay.isWorkday}
                                            label={workingDay.name}
                                            disabled={disableBugsAndWorkingDays || disabled}
                                        />
                                    );
                                })}
                            </div>
                        </FormItem>
                    );
                }}
            </Observer>
        );
    };

    private onSectionClicked = (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>, section: IAccordionSection): void => {
        const collapsed = section.isCollapsed as ObservableValue<boolean>;
        collapsed.value = !collapsed.value;
    };

    private onTeamSettingsChange = () => {
        this.updateBugsAndWorkingDays();
        return true;
    };

    private validate = (settings: ITeamPanelSettings) => {
        const valid = this.validateBacklogLevels(settings) && this.validateWorkingDays(settings);
        getTeamSettingsPanelService(this.context.pageContext).setIsValid(valid);
    };

    private validateBacklogLevels = (settings: ITeamPanelSettings): boolean => {
        if (!settings.backlogVisibilitySettings.find(setting => setting.checked.value)) {
            this.backlogLevelValidationMessage.value = Resources.BacklogsValidationMessage;
            this.backlogLevelValidationErrorState.value = true;
        } else {
            this.backlogLevelValidationMessage.value = "";
            this.backlogLevelValidationErrorState.value = false;
        }

        return !this.backlogLevelValidationErrorState.value;
    };

    private validateWorkingDays = (settings: ITeamPanelSettings): boolean => {
        if (!this.disableBugsAndWorkingDays.value && !settings.workingDays.find(setting => setting.isWorkday.value)) {
            this.workingDaysValidationMessage.value = Resources.WorkingDaysValidationMessage;
            this.workingDaysValidationErrorState.value = true;
        } else {
            this.workingDaysValidationMessage.value = "";
            this.workingDaysValidationErrorState.value = false;
        }

        return !this.workingDaysValidationErrorState.value;
    };

    private updateBugsAndWorkingDays() {
        const backlogVisibilitySettings = this.teamSettings.value?.backlogVisibilitySettings;
        if (backlogVisibilitySettings && !backlogVisibilitySettings.find(item => item.isRequirementmentBacklog && item.checked.value)) {
            // disable bug behavior and working days inputs if the requirements backlog is not visible
            this.disableBugsAndWorkingDays.value = true;
        }
    }
}

function getSectionContent(description: string, descriptionLink: ILinkProps | undefined, content: JSX.Element) {
    return (
        <div className="flex-column rhythm-vertical-16 body-m">
            <div>
                <span>{description}</span>
                {descriptionLink && (
                    <span>
                        <span> </span>
                        <Link href={descriptionLink.href} underline={true}>
                            {descriptionLink.title}
                        </Link>
                    </span>
                )}
            </div>
            <div>{content}</div>
        </div>
    );
}
